/**
 * Utilitaires pour la gestion des prix avec précision à 4 décimales
 * Conformément à la migration database NUMERIC(10,4) et NUMERIC(12,4)
 */

/**
 * Arrondit un prix à 4 décimales (précision de la base de données)
 * @param price Prix à arrondir
 * @returns Prix arrondi à 4 décimales
 */
export const roundToFourDecimals = (price: number): number => {
  return parseFloat(price.toFixed(4));
};

/**
 * Formate un prix pour l'affichage avec le bon nombre de décimales
 * - Affiche 4 décimales si nécessaire (valeurs < 0.01 ou avec des décimales significatives)
 * - Affiche 2 décimales pour les prix standard
 * 
 * @param price Prix à formater
 * @param withCurrency Ajouter le symbole € (défaut: true)
 * @returns Prix formaté
 */
export const formatPrice = (price: number, withCurrency: boolean = true): string => {
  // Si le prix a des décimales significatives au-delà de 2 décimales, afficher 4 décimales
  const needsFourDecimals = price < 0.01 || (price * 10000) % 100 !== 0;
  const decimals = needsFourDecimals ? 4 : 2;
  const formatted = price.toFixed(decimals);
  
  return withCurrency ? `${formatted} €` : formatted;
};

/**
 * Formate un prix en fonction de l'unité et du lot technique
 * - Pour lot 105 avec unité "€ (Budget)" : 4 décimales
 * - Sinon : 2 décimales par défaut (mais peut afficher 4 si < 0.01)
 * 
 * @param prix Prix à formater
 * @param unite Unité de l'article
 * @param idLotTechnique ID du lot technique
 * @param withCurrency Ajouter le symbole € (défaut: true)
 * @returns Prix formaté
 */
export const formatPrixByLot = (
  prix: number,
  unite?: string,
  idLotTechnique?: number,
  withCurrency: boolean = true
): string => {
  // Forcer 4 décimales pour lot 105 avec unité "€ (Budget)"
  const forceFourDecimals = idLotTechnique === 105 && unite === "€ (Budget)";
  
  if (forceFourDecimals) {
    const formatted = prix.toFixed(4);
    return withCurrency ? `${formatted} €` : formatted;
  }
  
  // Sinon, utiliser la logique intelligente
  return formatPrice(prix, withCurrency);
};

/**
 * Calcule le total HT avec précision à 4 décimales
 * @param prixUnitaire Prix unitaire
 * @param quantite Quantité
 * @returns Total HT arrondi à 4 décimales
 */
export const calculateTotalHT = (prixUnitaire: number, quantite: number): number => {
  return roundToFourDecimals(prixUnitaire * quantite);
};

/**
 * Formate un prix pour l'affichage en français (espace comme séparateur de milliers, virgule pour décimales)
 * @param price Prix à formater
 * @param decimals Nombre de décimales (défaut: intelligent)
 * @param withCurrency Ajouter le symbole € (défaut: true)
 * @returns Prix formaté à la française
 */
export const formatPriceFR = (
  price: number,
  decimals?: number,
  withCurrency: boolean = true
): string => {
  const dec = decimals ?? (price < 0.01 || (price * 10000) % 100 !== 0 ? 4 : 2);
  const formatted = price.toLocaleString('fr-FR', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  });
  
  return withCurrency ? `${formatted} €` : formatted;
};
